import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import checkPermission from "../../utils/permission";
import { ApiService } from "../../services/api";
import DepartmentSelector from "../../components/selectors/DepartmentSelector";

export default function NewUserPage() {
  const navigate = useNavigate();
  const hasCreatePermission = checkPermission("user_add");

  useEffect(() => {
    if (!hasCreatePermission) {
      navigate("/employees");
    }
  }, [hasCreatePermission, navigate]);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    departmentId: null as string | null,
    metadata: {
      schoolNumber: "",
      tcNumber: "",
      parentPhone: "",
      parentEmail: "",
    },
    paymentStatus: 1,
    licenseDays: null as number | null,
    isUnlimitedLicense: true,
    licenseExpiryDate: null as string | null,
    lastPaymentDate: null as string | null,
    isBlock: false,
  });

  const [file, setFile] = useState<File | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const fieldClassName =
    "w-full rounded-xl border border-slate-200/90 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-800 shadow-sm transition placeholder:text-slate-400 hover:border-slate-300 hover:bg-white focus:border-emerald-300 focus:bg-white focus:outline-none focus:ring-4 focus:ring-emerald-50";
  const sectionClassName = "rounded-xl border border-slate-200 bg-white p-4 sm:p-5";
  const sectionBadgeClassName =
    "inline-flex items-center rounded-full border border-emerald-100 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700";

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!form.firstName || !form.lastName || !form.email || !form.password || !form.departmentId) {
      setError("Lütfen zorunlu alanları doldurun.");
      return;
    }

    // Şifre uzunluğu validasyonu
    if (form.password.length < 8) {
      setError("Şifre en az 8 karakter olmalıdır.");
      return;
    }

    if (form.metadata?.tcNumber && form.metadata.tcNumber.length !== 11) {
      setError("TC Kimlik Numarası tam 11 rakam olmalıdır.");
      return;
    }

    if (form.phone && form.phone.length !== 10) {
      setError("Kişinin Telefon Numarası tam 10 rakam olmalıdır.");
      return;
    }

    if (form.metadata?.parentPhone && form.metadata.parentPhone.length !== 10) {
      setError("Veli Telefon Numarası tam 10 rakam olmalıdır.");
      return;
    }

    if (!form.isUnlimitedLicense && (!form.licenseDays || form.licenseDays < 1)) {
      setError("Süreli lisans için geçerli bir lisans gün sayısı girin.");
      return;
    }
    try {
      setLoading(true);
      await ApiService.createUser(
        {
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          phone: form.phone,
          password: form.password,
          departmentId: form.departmentId || null,
          metadata: form.metadata || {},
          paymentStatus: form.paymentStatus,
          licenseDays: form.isUnlimitedLicense ? null : form.licenseDays,
          isUnlimitedLicense: form.isUnlimitedLicense,
        },
        file
      );
      navigate("/employees?tab=users&created=" + Date.now());
    } catch (err: any) {
      setError(err.message || "Kullanıcı oluşturulamadı.");
    } finally {
      setLoading(false);
    }
  };

  if (!hasCreatePermission) {
    return null;
  }

  return (
    <div className="h-[88vh] bg-gray-100 overflow-hidden flex flex-col">
      <nav className="bg-white border-b border-gray-200 shadow-sm flex-shrink-0">
        <div className="px-6 py-3">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900">Üyeler</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-600">
              <button
                onClick={() => navigate("/employees?tab=users")}
                className={`pb-3 transition-all duration-200 text-gray-900 font-semibold border-b-2 border-gray-900`}>
                Kullanıcılar
              </button>
              <button
                onClick={() => navigate("/employees?tab=departments")}
                className={`pb-3 transition-all duration-200 text-gray-500 hover:text-gray-900`}>
                Departmanlar
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex-1 overflow-y-auto p-5 sm:p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6">
            <div className="mb-5 flex items-center justify-between gap-3">
              <div>
                <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Yeni Kullanıcı Ekle</h1>
                <p className="text-sm text-gray-600 mt-1">Kullanıcı bilgilerini doldurarak yeni hesap oluşturun.</p>
                <div className="mt-3 h-1 w-20 rounded-full bg-emerald-200" />
              </div>
              <button
                type="button"
                onClick={() => navigate("/employees?tab=users")}
                className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50">
                Geri Dön
              </button>
            </div>

            {error && <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-lg">{error}</div>}

            <form id="create-user-form" onSubmit={handleSubmit} className="space-y-5">
              <div className={sectionClassName}>
                <div className="mb-3">
                  <span className={sectionBadgeClassName}>
                    Temel Bilgiler
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-3">Profil Fotoğrafı</label>
                  <label className="group block cursor-pointer">
                    <div className="rounded-2xl border border-emerald-100 bg-gradient-to-b from-emerald-50 to-white p-4">
                      <div className="mx-auto h-36 w-36 rounded-full border-2 border-dashed border-emerald-200 bg-white shadow-sm overflow-hidden flex items-center justify-center transition group-hover:border-emerald-300 group-hover:shadow">
                        {file ? (
                          <img src={URL.createObjectURL(file)} alt="avatar" className="h-full w-full object-cover" />
                        ) : (
                          <span className="text-xs text-slate-400">Fotoğraf Önizleme</span>
                        )}
                      </div>
                      <div className="mt-3 flex items-center justify-between gap-2">
                        <span className="text-xs text-slate-500">JPG veya PNG</span>
                        <span className="rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 transition group-hover:border-emerald-300 group-hover:text-emerald-700">
                          Dosya Seç
                        </span>
                      </div>
                    </div>
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0])} />
                  </label>
                </div>
                <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Ad *</label>
                    <input
                      name="firstName"
                      value={form.firstName}
                      onChange={handleChange}
                      className={fieldClassName}
                      placeholder="Ahmet"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Soyad *</label>
                    <input
                      name="lastName"
                      value={form.lastName}
                      onChange={handleChange}
                      className={fieldClassName}
                      placeholder="Yılmaz"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">E-posta *</label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      className={fieldClassName}
                      placeholder="ahmet.yilmaz@gmail.com"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Telefon Numarası</label>
                    <input
                      name="phone"
                      value={form.phone}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "");
                        if (value.length <= 10) {
                          setForm((prev) => ({
                            ...prev,
                            phone: value,
                          }));
                        }
                      }}
                      className={fieldClassName}
                      placeholder="5321234567"
                      maxLength={10}
                      pattern="[0-9]{10}"
                      title="Telefon Numarası tam 10 rakam olmalıdır (örn: 5321234567)"
                    />
                    {form.phone && form.phone.length !== 10 && (
                      <p className="mt-1.5 text-xs text-red-600">Telefon Numarası tam 10 rakam olmalıdır</p>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Şifre *</label>
                    <input
                      type="password"
                      name="password"
                      value={form.password}
                      onChange={handleChange}
                      className={fieldClassName}
                      placeholder="En Az 8 Karakter"
                      required
                      minLength={8}
                      title="Şifre en az 8 karakter olmalıdır"
                    />
                    {form.password && form.password.length < 8 && (
                      <p className="mt-1.5 text-xs text-red-600">Şifre en az 8 karakter olmalıdır</p>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Departman *</label>
                    <DepartmentSelector
                      value={form.departmentId}
                      onChange={(departmentId) =>
                        setForm((p) => ({
                          ...p,
                          departmentId: departmentId || null,
                        }))
                      }
                      placeholder="Departman Seçin"
                      className="w-full rounded-xl"
                      required={true}
                      allowClear={false}
                      showColors={true}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Okul Numarası</label>
                    <input
                      name="metadata.schoolNumber"
                      value={form.metadata?.schoolNumber}
                      onChange={(e) => {
                        setForm((prev) => ({
                          ...prev,
                          metadata: {
                            ...prev.metadata,
                            schoolNumber: e.target.value,
                          },
                        }));
                      }}
                      className={fieldClassName}
                      placeholder="123"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">TC Kimlik Numarası</label>
                    <input
                      name="metadata.tcNumber"
                      value={form.metadata?.tcNumber}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "");
                        if (value.length <= 11) {
                          setForm((prev) => ({
                            ...prev,
                            metadata: {
                              ...prev.metadata,
                              tcNumber: value,
                            },
                          }));
                        }
                      }}
                      className={fieldClassName}
                      placeholder="12345678901"
                      maxLength={11}
                      pattern="[0-9]{11}"
                      title="TC Kimlik Numarası tam 11 rakam olmalıdır"
                    />
                    {form.metadata?.tcNumber && form.metadata.tcNumber.length !== 11 && (
                      <p className="mt-1.5 text-xs text-red-600">TC Kimlik Numarası tam 11 rakam olmalıdır</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Veli Telefon Numarası</label>
                    <input
                      name="metadata.parentPhone"
                      value={form.metadata?.parentPhone}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "");
                        if (value.length <= 10) {
                          setForm((prev) => ({
                            ...prev,
                            metadata: {
                              ...prev.metadata,
                              parentPhone: value,
                            },
                          }));
                        }
                      }}
                      className={fieldClassName}
                      placeholder="5321234567"
                      maxLength={10}
                      pattern="[0-9]{10}"
                      title="Veli Telefon Numarası tam 10 rakam olmalıdır (örn: 5321234567)"
                    />
                    {form.metadata?.parentPhone && form.metadata.parentPhone.length !== 10 && (
                      <p className="mt-1.5 text-xs text-red-600">Veli Telefon Numarası tam 10 rakam olmalıdır</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Veli E-posta</label>
                    <input
                      type="email"
                      name="metadata.parentEmail"
                      value={form.metadata?.parentEmail}
                      onChange={(e) => {
                        setForm((prev) => ({
                          ...prev,
                          metadata: {
                            ...prev.metadata,
                            parentEmail: e.target.value,
                          },
                        }));
                      }}
                      className={fieldClassName}
                      placeholder="mehmet.yilmaz@gmail.com"
                    />
                  </div>
                </div>
              </div>
              </div>

              <div className={sectionClassName}>
                <div className="mb-3">
                  <span className={sectionBadgeClassName}>
                    Lisans ve Odeme
                  </span>
                </div>
                <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-4">Lisans ve Ödeme Bilgileri</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Ödeme Durumu</label>
                    <select
                      name="paymentStatus"
                      value={form.paymentStatus}
                      onChange={(e) => {
                        const paymentStatus = parseInt(e.target.value, 10);
                        setForm((prev) => ({
                          ...prev,
                          paymentStatus,
                        }));
                      }}
                      className={fieldClassName}>
                      <option value={0}>Ödeme Yapılmadı</option>
                      <option value={1}>Ödeme Bekleniyor</option>
                      <option value={2}>Ödeme Yapıldı</option>
                      <option value={3}>Dilekçeli</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Lisans Tipi</label>
                    <select
                      value={form.isUnlimitedLicense ? "unlimited" : "limited"}
                      onChange={(e) => {
                        const isUnlimitedLicense = e.target.value === "unlimited";
                        setForm((prev) => ({
                          ...prev,
                          isUnlimitedLicense,
                          licenseDays: isUnlimitedLicense ? null : prev.licenseDays || 7,
                        }));
                      }}
                      className={fieldClassName}>
                      <option value="unlimited">Sınırsız Lisans</option>
                      <option value="limited">Süreli Lisans</option>
                    </select>
                  </div>
                </div>

                {!form.isUnlimitedLicense && (
                  <div className="mt-5 max-w-md">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Lisans Gün Sayısı *</label>
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={form.licenseDays || ""}
                      onChange={(e) => {
                        const rawValue = e.target.value;
                        const licenseDays = rawValue ? parseInt(rawValue, 10) : null;
                        setForm((prev) => ({
                          ...prev,
                          licenseDays,
                        }));
                      }}
                      className={fieldClassName}
                      placeholder="30"
                      required
                    />
                  </div>
                )}

                <p className="mt-3 text-xs text-slate-500">Yeni kullanıcı varsayılan olarak odeme bekleniyor durumunda oluşturulur.</p>
              </div>

              <div className={sectionClassName}>
                <div className="mb-3">
                  <span className={sectionBadgeClassName}>
                    Hesap Guvenligi
                  </span>
                </div>
                <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-4">Hesap Durumu</h3>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.isBlock}
                      disabled
                      className="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 rounded cursor-not-allowed"
                    />
                    <span className="text-sm font-medium text-gray-700">Hesabı Engelle</span>
                  </label>
                </div>

                <p className="mt-3 text-xs text-slate-500">
                  Yeni kullanıcı varsayılan olarak <strong>aktif</strong> durumda oluşturulur.
                </p>
              </div>

              <div className="sticky bottom-0 z-10 -mx-2 rounded-xl border border-slate-200 bg-white/95 p-3 backdrop-blur">
                <div className="flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="px-4 py-2 bg-white text-gray-700 rounded-md border border-gray-300 hover:bg-gray-50 text-sm">
                    İptal
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-md hover:from-emerald-700 hover:to-teal-700 disabled:opacity-60 text-sm">
                    {loading ? "Kaydediliyor..." : "Kullanıcı Oluştur"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
